// src/AssetManager/AssetDetailsDialog.jsx used as a lightbox UI for viewing DAM asset details
import React from "react";
import Dialog from "@mui/material/Dialog";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";          // real MUI button for Edit/Save
import CloseIcon from "@mui/icons-material/Close";
import CircularProgress from "@mui/material/CircularProgress";
import { useNavigate } from "react-router-dom";
import { OrganizationProvider2 } from "../contexts/OrganizationContext2";
import { UserManagementProvider } from "../contexts/UserManagementContext";
import { FormProvider } from "../contexts/FormContext";
import AssetDetailsView from "../pages/AssetDetailsView";

export default function AssetDetailsDialog() {
  const navigate = useNavigate();
  const handleClose = () => navigate(-1);

  const [isEditing, setIsEditing] = React.useState(false);
  const [saving, setSaving] = React.useState(false); // ← keeps the spinner state

  // we disable the button immediately but only show the spinner if the save lasts > debounce
  const [pendingSave, setPendingSave] = React.useState(false);        // disable button immediately
  const pendingTimerRef = React.useRef(null);                          // debounce timer holder

  // stay in sync with page’s edit state (AssetDetailsView broadcasts assetdetails:status)
  React.useEffect(() => {
    const onStatus = (e) => {
      const d = e?.detail || {};
      if (d?.route === "asset-details") {
        setIsEditing(!!d.editing);
      }
    };
    window.addEventListener("assetdetails:status", onStatus);
    return () => window.removeEventListener("assetdetails:status", onStatus);
  }, []);

  // Listen for submit completion → clear "saving" (and pending) on success/failure
  React.useEffect(() => {
    const onSubmitStatus = (e) => {
      // Always clear spinner/debounce when we hear a status
      if (pendingTimerRef.current) {
        clearTimeout(pendingTimerRef.current);
        pendingTimerRef.current = null;
      }
      setPendingSave(false);
      setSaving(false);

      // Stay in edit after save → no extra UI flip, no second "blip"
      // (Do nothing here; the page shows a toast. Users can close or keep editing.)
    };
    window.addEventListener("assetdetails:submit-status", onSubmitStatus);
    return () => window.removeEventListener("assetdetails:submit-status", onSubmitStatus);
  }, []);

  // ← NEW: clean up any lingering debounce timer on unmount
  React.useEffect(() => {
    return () => {
      if (pendingTimerRef.current) {
        clearTimeout(pendingTimerRef.current);
        pendingTimerRef.current = null;
      }
    };
  }, []);

  // ───────────────────────────────────────────────────────────────
  // Tell the rest of the app the dialog is open/closed
  //  • AssetBoard (or any background refresh) can pause when open
  //  • We broadcast on mount, and clear on unmount
  // ───────────────────────────────────────────────────────────────
  React.useEffect(() => {
    try {
      window.dispatchEvent(
        new CustomEvent("assetdetails:dialog-open", { detail: { open: true } })
      );
    } catch {}
    return () => {
      try {
        window.dispatchEvent(
          new CustomEvent("assetdetails:dialog-open", { detail: { open: false } })
        );
      } catch {}
    };
  }, []);


  // Click handler for the top-right button
  const handlePrimaryClick = React.useCallback(() => {
    try {
      if (!isEditing) {
        // Enter edit mode
        window.dispatchEvent(
          new CustomEvent("assetdetails:set-edit", { detail: { value: true } })
        );
        setIsEditing(true); // optimistic
        return;
      }
      // Already editing → SAVE:
      if (saving || pendingSave) return; // guard against double clicks

      // ← NEW: disable immediately, but only show spinner if save takes longer than 120ms
      setPendingSave(true);
      pendingTimerRef.current = setTimeout(() => {
        setSaving(true);      // shows spinner only for genuinely slow saves
      }, 120);

      // Ask AssetDetailsView to submit using its existing submit pipeline
      window.dispatchEvent(new CustomEvent("assetdetails:request-submit"));
      // NOTE: We'll clear pending/spinner when AssetDetailsView posts 'assetdetails:submit-status'
    } catch {}
  }, [isEditing, saving, pendingSave]);

  return (
    <Dialog
      open
      onClose={handleClose}
      fullScreen
      PaperProps={{ sx: { bgcolor: "rgba(0,0,0,0.92)" } }}
    >
      {/* Floating header controls (overlay)
         - zIndex: 1301 → above dialog content (~1300), but BELOW VersionsDrawer (≈1302).
         - pointer-events: none on wrapper so it never blocks scroll/hover; re-enable on buttons.
         - Fixed to true top-right (8px, 8px)
      */}
      <div
        style={{
          position: "fixed",
          top: 8,
          right: 8,
          zIndex: 1301,
          display: "flex",
          gap: 8,
          pointerEvents: "none",
        }}
      >
        <Button
          size="small"
          variant="contained"
          onClick={handlePrimaryClick}
          aria-label={isEditing ? "Save changes" : "Edit asset metadata"}
          disabled={saving || pendingSave}                             
          sx={{
            textTransform: "none",
            fontWeight: 600,
            boxShadow: "none",
            pointerEvents: "auto",
            minWidth: 92,
          }}
        >
          {isEditing ? (
            saving ? (
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <CircularProgress size={14} />
                Saving
              </span>
            ) : (
              "Save"  // while pendingSave is true but saving=false, we still show "Save" (no spinner)
            )
          ) : (
            "Edit"
          )}
        </Button>

        <IconButton
          size="small"
          onClick={handleClose}
          aria-label="Close"
          sx={{
            color: "#fff",
            bgcolor: "rgba(255,255,255,0.14)",
            "&:hover": { bgcolor: "rgba(255,255,255,0.22)" },
            pointerEvents: "auto",
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </div>

      {/* Centered card (lightbox content) */}
      <div
        style={{
          width: "100%",
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          // Top | Sides | Bottom: keep exactly as you like
          padding: "42px 12px 16px",
          boxSizing: "border-box",
        }}
      >
        <div
          style={{
            // Wider on small screens, capped on large displays
            width: "min(1280px, 99vw)",
            height: "min(90vh, 980px)",
            background: "#fff",
            borderRadius: 12,
            overflow: "hidden",
            boxShadow: "0 8px 24px rgba(0,0,0,0.45)",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div style={{ flex: 1, overflow: "auto" }}>
            <OrganizationProvider2>
              <UserManagementProvider>
                <FormProvider>
                  <AssetDetailsView />
                </FormProvider>
              </UserManagementProvider>
            </OrganizationProvider2>
          </div>
        </div>
      </div>
    </Dialog>
  );
}
